/**
 * Token Management Utility
 * Handles token refresh for HttpOnly cookie-based auth.
 *
 * The access_token and refresh_token are HttpOnly cookies managed by the server.
 * Client-side JS cannot read or inspect them. Therefore:
 *  - No client-side expiry checks (we can't read cookie expiry)
 *  - Proactive refresh on a fixed interval shorter than access-token lifetime
 *  - Reactive refresh on 401 (handled by apiClient.js)
 */

const PROACTIVE_REFRESH_INTERVAL = 50 * 60 * 1000; // 50 minutes (access_token lives 60 min)

/**
 * Refresh token with backend
 * @param {string} backendURL - Backend URL
 * @param {Function} onTokenRefreshed - Callback when token refreshed
 * @param {Function} onRefreshFailed - Callback when refresh fails
 * @returns {Promise<boolean>} True if refresh successful
 */
export const refreshTokenWithBackend = async (backendURL, onTokenRefreshed, onRefreshFailed) => {
  try {
    // Rely exclusively on HttpOnly cookies - no client-side token storage
    const storedRefreshToken = typeof window !== 'undefined' ? sessionStorage.getItem('refresh_token') : null;
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    };
    if (storedRefreshToken) headers['X-Refresh-Token'] = storedRefreshToken;

    const response = await fetch(`${backendURL}/api/refresh-token/`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: storedRefreshToken ? JSON.stringify({ refresh_token: storedRefreshToken }) : undefined,
    });
    if (!response.ok) throw new Error(`Token refresh failed: ${response.status}`);

    const data = await response.json().catch(() => ({}));
    if (data.id_token || data.access_token) {
      sessionStorage.setItem('access_token', data.id_token || data.access_token);
    }
    if (data.refresh_token) {
      sessionStorage.setItem('refresh_token', data.refresh_token);
    }
    if (onTokenRefreshed) onTokenRefreshed();
    return true;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    if (onRefreshFailed) onRefreshFailed();
    return false;
  }
};

/**
 * Exponential backoff retry for token refresh
 * @param {string} backendURL - Backend URL
 * @param {Function} onSuccess - Callback on success
 * @param {Function} onFailure - Callback on final failure
 * @param {number} maxRetries - Maximum retry attempts
 */
export const refreshTokenWithRetry = async (
  backendURL,
  onSuccess,
  onFailure,
  maxRetries = 3
) => {
  let retryCount = 0;

  const attemptRefresh = async () => {
    const success = await refreshTokenWithBackend(
      backendURL,
      onSuccess,
      null // Don't call failure callback yet
    );

    if (success) {
      return true;
    }

    retryCount++;

    if (retryCount >= maxRetries) {
      console.error('Token refresh failed after maximum retries');
      if (onFailure) onFailure();
      return false;
    }

    // Exponential backoff with jitter: 1s, 2s, 4s, plus up to 250ms jitter
    const baseDelay = Math.pow(2, retryCount) * 1000;
    const jitter = Math.floor(Math.random() * 250);
    const delay = baseDelay + jitter;

    await new Promise(resolve => setTimeout(resolve, delay));
    return attemptRefresh();
  };

  return attemptRefresh();
};

/**
 * Clear client-side auth artifacts (localStorage, sessionStorage).
 * Cannot clear HttpOnly cookies — the server does that via /api/logout/.
 */
export const clearToken = () => {
  try {
    const authKeys = ['access_token', 'refresh_token', 'graph_token'];
    authKeys.forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    console.error('Failed to clear token:', error);
  }
};

/**
 * Start proactive token refresh on a fixed interval.
 * Calls the backend refresh endpoint every 50 minutes to keep the
 * access_token cookie alive without waiting for a 401.
 *
 * @param {string} backendURL - Backend URL
 * @param {Function} onRefreshFailed - Called when refresh fails (trigger logout)
 * @returns {Function} Cleanup function to stop the interval
 */
export const startProactiveRefresh = (backendURL, onRefreshFailed) => {
  if (!backendURL) {
    console.warn('startProactiveRefresh: no backendURL provided');
    return () => { };
  }

  const intervalId = setInterval(async () => {
    await refreshTokenWithRetry(
      backendURL,
      null,
      () => {
        console.error('[TokenManager] Proactive refresh failed — logging out');
        if (onRefreshFailed) onRefreshFailed();
      }
    );
  }, PROACTIVE_REFRESH_INTERVAL);

  return () => {
    clearInterval(intervalId);
  };
};

export default {
  refreshTokenWithBackend,
  refreshTokenWithRetry,
  clearToken,
  startProactiveRefresh,
};
